import { AiChat, PersonaOptions, StreamSend, useAsStreamAdapter, useAiChatApi, AiChatUI } from "@nlux/react";
import { Spin, Tag, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "styles/vendor/nlux.css";
import "./theme-variables.css";
import "./MistralChat.css";
import { MistralResponseRenderer } from "./MistralResponseRenderer";
import { MistralPromptRenderer } from "./MistralPromptRenderer";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { axios } from "General/axios";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";

export const MistralChat = ({ fileId }: { fileId: number }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [configurationError, setConfigurationError] = useState(false);

  const aiChatApi = useAiChatApi();

  const streamText: StreamSend<string> = async (message, observer, { conversationHistory }) => {
    try {
      if (!message) {
        observer.error(new Error("Please enter a message"));
        return;
      }
      const messages: API_Message[] = [
        ...conversationHistory.map((item) => {
          if (item.role === "user") {
            const textContent = Array.isArray(item.message)
              ? [{ type: "text" as const, text: item.message.join("") }]
              : [{ type: "text" as const, text: item.message }];

            return {
              role: item.role,
              content: [...textContent],
            };
          } else {
            // For assistant messages, only include text content
            return {
              role: item.role,
              content: Array.isArray(item.message)
                ? [{ type: "text" as const, text: item.message.join("") }]
                : [{ type: "text" as const, text: item.message }],
            };
          }
        }),
        {
          role: "user",
          content: [
            {
              type: "text",
              text: message,
            },
          ],
        },
      ];

      const payload: API_AiChatRequest = {
        messages: messages,
      };

      const stream = await axios.request({
        headers: API.headersObject(),
        url: API_URL.AI_CHAT.chatStream(projectsDataStore.projectId, fileId),
        method: "POST",
        data: payload,
        responseType: "stream",
        adapter: "fetch",
      });
      const decoder = new TextDecoder();
      for await (const chunk of stream.data) {
        if (chunk) {
          // Decode the Uint8Array to string
          const decodedChunk = decoder.decode(chunk, { stream: true });

          // Parse SSE (Server-Sent Events) format
          const lines = decodedChunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.substring(6); // Remove "data: " prefix

              // Check for stream end marker
              if (data === "[DONE]") {
                continue;
              }

              try {
                const parsedChunk = JSON.parse(data);
                const deltaContent = parsedChunk.choices[0]?.delta?.content;
                if (typeof deltaContent === "string" && deltaContent.length > 0) {
                  observer.next(deltaContent);
                }
              } catch (e) {
                console.error("Error parsing SSE data:", e, "Data:", data);
              }
            }
          }
        }
      }
      observer.complete();
    } catch (error) {
      console.error("Error in streamText:", error);
      observer.error(error);
      throw error;
    }
  };

  const streamAdapter = useAsStreamAdapter(streamText, [fileId]);

  const validateConfiguration = useCallback(async () => {
    try {
      await API.getData(API_URL.AI_CHAT.validateConfiguration(projectsDataStore.projectId, fileId));
      setConfigurationError(false);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setConfigurationError(true);
      }
    }
  }, [fileId]);

  useEffect(() => {
    validateConfiguration();
  }, [fileId, validateConfiguration]);

  const personas: PersonaOptions = {
    user: {
      name: "User",
      avatar: <FontAwesomeIcon icon={["fas", "user"]} fixedWidth size="2x" />,
    },
    assistant: {
      name: t("Welcome to your PAVE AI Assistant"),
      avatar: <FontAwesomeIcon icon={["fas", "user-robot"]} fixedWidth size="2x" />,
      tagline: configurationError
        ? // library supports string or JSX.Element in tagline but in typescript interface it is string only
          ((
            <Typography.Text type="danger" strong>
              {t("Some configurations are missing. Please contact an administrator or the support team.")}
            </Typography.Text>
          ) as unknown as string)
        : // library supports string or JSX.Element in tagline but in typescript interface it is string only
          ((
            <>
              {t(
                "You can use it to summarize the document, translate it, or extract information in a specific format."
              )}
              <br />
              {t("Remember that AI responses may be incorrect or incomplete.")}
            </>
          ) as unknown as string),
    },
  };

  return (
    <div className="mistral-chat">
      <div className="mistral-chat__header">
        <FontAwesomeIcon icon={["fas", "user-robot"]} fixedWidth className="mistral-chat__header-icon" />
        <span className="mistral-chat__title">{t("PAVE AI Assistant")}</span>
        <Tag className="mistral-chat__beta-tag">{t("Beta")}</Tag>
        <span className="mistral-chat__spacer" />
        <FontAwesomeIcon icon={["fas", "user"]} fixedWidth className="mistral-chat__profile-icon" />
      </div>
      <div className="mistral-chat__body">
        <AiChat
          api={aiChatApi}
          adapter={streamAdapter}
          personaOptions={personas}
          className={classNames({ ["nlux-disabled"]: configurationError })}
          displayOptions={{ colorScheme: "light", themeId: "pave" }}
          conversationOptions={{ layout: "bubbles", historyPayloadSize: 8, autoScroll: true }}
          composerOptions={{
            autoFocus: true,
            placeholder: t("Ask me anything about this document"),
            disableSubmitButton: configurationError,
          }}
          messageOptions={{
            skipStreamingAnimation: true,
            responseRenderer: (props) => <MistralResponseRenderer {...props} />,
            promptRenderer: MistralPromptRenderer,
          }}
        >
          <AiChatUI.Loader>
            <Spin spinning />
          </AiChatUI.Loader>
        </AiChat>
      </div>
    </div>
  );
};
